import React, { useContext, useEffect, useState } from "react";
import { LAText, LAView } from "../../components";
import { useNavigate } from "react-router-dom";
import { IMAGES, SVG_IMAGES } from "../../assets";
import { STRINGS } from "../../constant";
import { GET_PRACTICE_TEST } from "../../../app_constant";
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import BackArrowIcon from "../../components/BackArrowIcon";
import PracticeTestDialog from "../../components/PracticeTestDialog";
import moment from "moment";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClipboardCheck, 
  faCalendarAlt, 
  faLock,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

const PracticeTest = () => {
  const { profile, questionsList, community } = useContext(AuthContext);
  const { selectedCertificate } = useContext(BundleContext);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  const { theme, currentTheme } = useContext(ThemeContext);
  const PRACTICE_TEST = GET_PRACTICE_TEST(selectedCertificate);
  const navigate = useNavigate();
  
  const handleTestClick = (item) => {
    const filterQuestions = (questionsList ?? []).filter(
      (question) => question?.practice_test_id === item?.practice_test_id
    );
    const num_questions = filterQuestions.length;

    const questionTime = profile?.time_per_question
      ? profile?.time_per_question
      : community?.total_time_per_question ?? 90;

    let test_time = questionTime * num_questions;
    item.test_time = test_time;
    item.is_custom_test = false;
    item.num_questions = num_questions;
    setSelectedTest(item);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedTest(null);
  };

  const handleTestStart = () => {
    localStorage.setItem("testStart", JSON.stringify(true));
    setIsDialogOpen(false);
    navigate("/test", { state: selectedTest });
  };

  const handleChoosePlan = () => {
    setIsDialogOpen(false);
    navigate("/chooseplan");
  };

  // Card animations
  const cardVariants = {
    hover: { 
      scale: 1.02,
      boxShadow: "0px 8px 15px rgba(0,0,0,0.1)",
      transition: { type: "spring", stiffness: 400, damping: 10 }
    }
  };

  // Helper function to get icon background color
  const getIconBg = () => {
    return currentTheme === 'light' ? theme?.icon_bg?.practice_light : theme?.icon_bg?.practice_dark;
  }

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          className="rounded-lg border-2 p-2 mr-4 flex items-center justify-center"
          style={{ borderColor: currentTheme === "light" ? theme?.border_color?.light : theme?.border_color?.dark }}
          onClick={() => navigate("/home")}
        >
          <BackArrowIcon
            color={currentTheme === "light" ? theme?.text?.light : theme?.text?.dark}
          />
        </button>
        <h1 
          className="text-2xl font-semibold"
          style={{ color: getTextColor() }}
        >
          {STRINGS.practice_test}
        </h1>
      </div>

      {/* Practice Tests Grid */}
      <div className="grid grid-cols-1 gap-4 mb-8">
        {PRACTICE_TEST?.map((practiceSet, index) => (
          <PracticeTestItem
            key={index}
            practiceSet={practiceSet}
            onClick={() => handleTestClick(practiceSet)}
            questionsList={questionsList}
            profile={profile}
            theme={theme}
            currentTheme={currentTheme}
          />
        ))}
      </div>

      {selectedTest && (
        <PracticeTestDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          onStart={handleTestStart}
          onChoosePlan={handleChoosePlan}
          testDetails={selectedTest}
          iconBg={getIconBg()}
        />
      )}
    </div>
  );
};

const PracticeTestItem = ({ practiceSet, onClick, questionsList, profile, theme, currentTheme }) => {
  const [totalCount, setQuestionCount] = useState(0);
  
  // Card animations
  const cardVariants = {
    hover: { 
      scale: 1.02,
      boxShadow: "0px 8px 15px rgba(0,0,0,0.1)",
      transition: { type: "spring", stiffness: 400, damping: 10 }
    }
  };

  useEffect(() => {
    getQuestionCount();
  }, []);

  async function getQuestionCount() {
    const filterQuestions = (questionsList ?? []).filter(
      (question) => question?.practice_test_id === practiceSet?.practice_test_id
    );
    setQuestionCount(filterQuestions.length);
  }

  function getLatestDateOfTest() {
    const tests = (profile?.practice_test_history ?? []).filter(
      (e) => e.test_name == practiceSet.test_name
    );
    if (tests?.length > 0) {
      const dates = tests.map((e) => moment(e.date, "MMM, DD YYYY"));
      return moment.max(dates).format("MMM, DD YYYY");
    }
    return null;
  }

  const latestDate = getLatestDateOfTest();
  
  // Helper function to get icon background color
  const getIconBg = () => {
    return currentTheme === 'light' ? theme?.icon_bg?.practice_light : theme?.icon_bg?.practice_dark;
  }

  // Helper function to get card background
  const getCardBackground = (iconBg) => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark;   
    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
      borderLeft: `4px solid ${iconBg || (currentTheme === 'light' ? theme?.icon_bg?.practice_light : theme?.icon_bg?.practice_dark)}`
    };
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  const iconBg = getIconBg();

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm"
      whileHover="hover"
      variants={cardVariants}
      onClick={onClick}
    >
      <div 
        className="cursor-pointer"
        style={getCardBackground(iconBg)}
      >
        <div className="p-4">
          <div className="flex items-center">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
              style={{ backgroundColor: iconBg }}
            >
              <FontAwesomeIcon 
                icon={faClipboardCheck} 
                className="text-white text-xl" 
              />
            </div>
            <div className="flex-1">
              <h2 
                className="text-lg font-medium"
                style={{ color: getTextColor() }}
              >
                {practiceSet.test_name}
              </h2>
              <div className="flex items-center mt-1">
                <span 
                  className="text-sm mr-4"
                  style={{ color: currentTheme === 'light' ? 'rgba(75,85,99,1)' : 'rgba(156,163,175,1)' }}
                >
                  {totalCount} Questions
                </span>
                {latestDate && (
                  <div className="flex items-center">
                    <FontAwesomeIcon 
                      icon={faCalendarAlt} 
                      className="mr-1 text-xs"
                      style={{ color: currentTheme === 'light' ? 'rgba(75,85,99,1)' : 'rgba(156,163,175,1)' }}
                    />
                    <span 
                      className="text-xs"
                      style={{ color: currentTheme === 'light' ? 'rgba(75,85,99,1)' : 'rgba(156,163,175,1)' }}
                    >
                      Last taken: {latestDate}
                    </span>
                  </div>
                )}
                {!latestDate && (
                  <span 
                    className="text-xs"
                    style={{ color: currentTheme === 'light' ? 'rgba(75,85,99,1)' : 'rgba(156,163,175,1)' }}
                  >
                    Not yet taken
                  </span>
                )}
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PracticeTest;